/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import './style.scss';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { getAllByFilter } from 'apis/programs.api';
import { changeProgram } from 'apis/projectProposals.api';
import { TOAST } from 'constant';
import { toast } from 'layout';

import Pagination from 'components/Pagination';
import { mapError, formatDate } from 'utils/func';
import ConfirmChangeProgram from '../ConfirmChangeProgram';

function ViewListProgram({
  onSelectedChange,
  setVisible,
  currentData,
  refetch: refetchProjectProposals,
}) {
  // #region data
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [filtersTable, setFiltersTable] = useState({
    _name: '',
  });
  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    name: '',
  });
  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    name: '',
  });
  const { mutateAsync: changeProgramMuteAsync, isLoading: isLoadingChangeProgram } =
    useMutation(changeProgram);

  const {
    data: _data,
    isLoading,
    isError,
    error,
  } = useQuery(
    ['program', pagination, appliedFilters],
    ({ queryKey: [, _pagination, _appliedFilters] }) => getAllByFilter(_pagination, _appliedFilters)
  );

  const data = useMemo(() => _data?.data ?? [], [_data, pagination, appliedFilters]);

  useEffect(() => {
    setSelectedProgram(currentData);
  }, [currentData]);

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-program-filters`) ?? '{}'
    );

    setFilters({
      ...localFilters,
    });
    setAppliedFilters({
      ...localFilters,
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );

    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
  }, []);

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-program-filters`,
          JSON.stringify(filters)
        );
      }}
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, error)}</h4>;
  }

  const textFilter = (field) => (
    <InputText
      value={filters[field]}
      onChange={(e) => {
        const newFilters = { ...filters, [field]: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t(`program.${field}`)}
      className="p-column-filter"
    />
  );

  const handleSelectionChange = (e) => {
    setSelectedProgram(e.value);
  };

  // Find selected program for DataTable selection
  const selectedData = useMemo(
    () => data?.data?.find((program) => program.id === selectedProgram?.id) || null,
    [data, selectedProgram]
  );

  const handleConfirmChange = async () => {
    try {
      await changeProgramMuteAsync({
        programId: selectedProgram.id,
        projectProposalId: currentData.projectProposalId,
      });
      setVisible(false);
      setShowConfirmDialog(false);
      toast(TOAST.SUCCESS, t('projectProposal.changeProgram.success'));
      refetchProjectProposals();
    } catch (err) {
      toast(TOAST.ERROR, mapError(t, err));
    }
  };

  return (
    <div className="relative p-formgroup-inline">
      <DataTable
        value={data?.data}
        className="program-list"
        dataKey="id"
        showGridlines
        scrollable
        selectionMode="single"
        selection={selectedData}
        onSelectionChange={handleSelectionChange}
        onFilterApplyClick={() => {
          setAppliedFilters(filters);
        }}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
      >
        <Column selectionMode="single" headerStyle={{ width: '3rem' }} frozen />

        <Column
          // eslint-disable-next-line react/prop-types
          body={(value, props) => props.rowIndex + 1}
          header={t('program.viewList.no')}
          className="text-center w-3rem"
          frozen
        />
        <Column
          field="name"
          header={t('program.name')}
          headerStyle={filterActiveStyle('name')}
          dataType="date"
          filter
          filterField="_name"
          filterElement={textFilter('name')}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, name: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-program-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem' }}
        />

        <Column
          filterField="_description"
          field="description"
          header={t('program.description')}
          headerStyle={filterActiveStyle('description')}
          style={{ minWidth: '14rem' }}
        />

        <Column
          header={t('program.startingTime')}
          body={(value) => formatDate(value?.startingTime)}
          className="w-10rem text-center"
        />
        <Column
          header={t('program.endingTime')}
          body={(value) => formatDate(value?.endingTime)}
          className="w-10rem text-center"
          field="endingTime"
          sortable
        />
        <Column
          header={t('program.organizationPrequalificationDeadline')}
          body={(value) => formatDate(value?.organizationPrequalificationDeadline)}
          className="w-10rem text-center"
        />
        <Column
          header={t('program.staffPrequalificationDeadline')}
          body={(value) => formatDate(value?.staffPrequalificationDeadline)}
          className="w-10rem text-center"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data?.pagination}
        pageName="projectProposals"
      />

      <div className="flex flex-wrap justify-content-end mt-4">
        <Button
          label={t('formLayout.action.cancel')}
          severity="danger"
          onClick={() => {
            setVisible(false);
            onSelectedChange(null);
          }}
          className="mr-1 w-8rem"
        />
        <Button
          label={t('formLayout.action.change')}
          severity="info"
          type="submit"
          className="w-8rem"
          onClick={() => {
            setShowConfirmDialog(true);
          }}
          disabled={!selectedProgram || selectedProgram?.id === currentData?.id}
        />
      </div>

      <ConfirmChangeProgram
        handleConfirmChange={handleConfirmChange}
        isVisible={showConfirmDialog}
        setIsVisible={setShowConfirmDialog}
        currentData={currentData}
        programName={selectedProgram?.name ?? ''}
        loading={isLoadingChangeProgram}
      />
    </div>
  );
  // #endregion Render
}

ViewListProgram.propTypes = {
  onSelectedChange: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  setVisible: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  currentData: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  refetch: PropTypes.func,
};
ViewListProgram.defaultProps = {
  onSelectedChange: () => true,
  currentData: null,
  setVisible: () => true,
};
export default ViewListProgram;
